import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, tokenStore } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPlatformCenterAccess } from "@/lib/postLoginRedirect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Building2, Upload, ArrowLeft } from "lucide-react";
import { BNTLogo } from "./layoutChrome";
import { setStoredClientId } from "@/lib/clientSelection";
import SecurityRecoveryQuestionFields from "@/components/SecurityRecoveryQuestionFields";
import { validateSecurityRecoveryRows } from "@/lib/commercialLicenseRequests";
import { formatRegimenSatSelectLabel, REGIMENES_SAT } from "@/lib/regimenesSat";

const DEFAULT_SECURITY_ROWS = [
  { q: "", answer: "" },
  { q: "", answer: "" },
  { q: "", answer: "" },
];

/** Extrae código SAT (601…) del texto del PDF o del catálogo. */
function normalizeRegimenCode(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  const m = s.match(/\b(60[1-9]|61[0-6]|62[0-6])\b/);
  return m ? m[1] : s.slice(0, 10);
}

export default function RegistroInicial() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [leavingLogin, setLeavingLogin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [csfBusy, setCsfBusy] = useState(false);
  const [csfWarnings, setCsfWarnings] = useState([]);
  const [secRows, setSecRows] = useState(DEFAULT_SECURITY_ROWS);
  const [form, setForm] = useState({
    razon_social: "",
    rfc: "",
    nombre_comercial: "",
    domicilio: "",
    responsable: "",
    telefono: "",
    codigo_postal: "",
    regimen_fiscal: "",
    correo_facturacion: "",
    email: "",
    new_password: "",
    new_password2: "",
    new_portal_username: "",
  });

  useEffect(() => {
    if (hasPlatformCenterAccess(user)) {
      navigate("/platform", { replace: true });
    }
  }, [user, navigate]);

  const onCsfPdfSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCsfBusy(true);
    setCsfWarnings([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clients/import-csf-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const { parse_warnings, text_preview, ok, ...fields } = data;
      setForm((f) => ({
        ...f,
        rfc: fields.rfc || f.rfc,
        razon_social: fields.razon_social || f.razon_social,
        regimen_fiscal: normalizeRegimenCode(fields.regimen_fiscal) || f.regimen_fiscal,
        codigo_postal: fields.codigo_postal || f.codigo_postal,
        domicilio: fields.direccion || fields.domicilio || f.domicilio,
      }));
      setCsfWarnings(Array.isArray(parse_warnings) ? parse_warnings : []);
      toast.success("Datos leídos del PDF. Revise los campos antes de guardar.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo leer el PDF");
    } finally {
      setCsfBusy(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (form.new_password !== form.new_password2) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (!form.rfc.trim() || form.rfc.trim().length < 10) {
      toast.error("Indica un RFC válido");
      return;
    }
    const secErr = validateSecurityRecoveryRows(secRows);
    if (secErr) {
      toast.error(secErr);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/system/trial-onboarding", {
        razon_social: form.razon_social.trim(),
        rfc: form.rfc.trim().toUpperCase(),
        nombre_comercial: form.nombre_comercial.trim(),
        domicilio: form.domicilio.trim(),
        responsable: form.responsable.trim(),
        telefono: form.telefono.trim(),
        codigo_postal: form.codigo_postal.trim(),
        regimen_fiscal: form.regimen_fiscal.trim(),
        correo_facturacion: form.correo_facturacion.trim(),
        email: form.email.trim().toLowerCase(),
        new_password: form.new_password,
        new_portal_username: form.new_portal_username.trim().toLowerCase().replace(/\s+/g, ""),
        security_questions: secRows.map((r) => ({ q: String(r.q || "").trim() })),
        security_answers: secRows.map((r) => String(r.answer || "").trim()),
      });
      if (data.access_token) {
        tokenStore.set(data.access_token, data.refresh_token, { remember: false });
      }
      setUser({
        id: data.id,
        email: data.email,
        name: data.name,
        role: data.role,
        is_admin_owner: data.is_admin_owner,
        is_buyer_tenant: data.is_buyer_tenant,
        tenant_buyer_id: data.tenant_buyer_id,
        portal_username: data.portal_username,
        needs_trial_registration: data.needs_trial_registration,
        trial_bootstrap: data.trial_bootstrap,
        trial_onboarding_completed_at: data.trial_onboarding_completed_at,
        can_generate_licenses: data.can_generate_licenses,
        can_switch_workspace: data.can_switch_workspace,
        has_global_client_supervision: data.has_global_client_supervision,
        scoped_workspace: data.scoped_workspace,
      });
      if (data.preferred_client_id) {
        setStoredClientId(data.preferred_client_id);
      }
      toast.success("Registro guardado. Ya puedes usar el sistema en periodo de prueba.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo completar el registro");
    } finally {
      setBusy(false);
    }
  };

  const returnToLogin = async () => {
    setLeavingLogin(true);
    try {
      await api.post("/auth/logout").catch(() => {});
    } finally {
      tokenStore.clear();
      setUser(false);
      setLeavingLogin(false);
      navigate("/login", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F8] py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-sm shadow-sm p-8">
        <p className="text-center text-xs text-slate-600 mb-4" data-testid="registro-back-to-login">
          ¿Ya tiene cuenta?{" "}
          <button
            type="button"
            disabled={leavingLogin}
            onClick={returnToLogin}
            className="text-[#0F2D5C] font-medium underline inline-flex items-center gap-1 disabled:opacity-50"
          >
            {leavingLogin ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowLeft className="w-3 h-3" />}
            Iniciar sesión
          </button>
        </p>

        <div className="flex items-center gap-3 mb-6">
          <Building2 className="w-10 h-10 text-[#0F2D5C]" />
          <div>
            <h1 className="text-xl font-bold text-[#0F2D5C]">Registro de su empresa</h1>
            <p className="text-xs text-slate-600 mt-1">
              Complete sus datos fiscales y defina el correo y la contraseña con los que ingresará al sistema.
            </p>
            <p className="text-[11px] text-slate-600 mt-2 leading-snug">
              El registro crea tu cuenta de comprador. Después podrás solicitar tu licencia web o generar una solicitud
              por PDF desde Configuración → Mi Negocio / Licencia.
            </p>
          </div>
        </div>
        <div className="mb-6 flex justify-center border-b border-slate-100 pb-6">
          <BNTLogo size="md" showDefault />
        </div>

        <form onSubmit={onSubmit} className="space-y-4 text-sm" data-testid="registro-inicial-form">
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/80 p-4 space-y-2">
            <Label className="text-xs font-semibold text-[#0F2D5C]">Constancia de situación fiscal (opcional)</Label>
            <p className="text-[11px] text-slate-600 leading-snug">
              Suba el PDF del SAT para precargar RFC, razón social, régimen y domicilio. Revise los datos antes de guardar.
            </p>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-sm p-4 cursor-pointer hover:border-[#3DA873] hover:bg-emerald-50/30 transition-colors">
              {csfBusy ? (
                <Loader2 className="w-7 h-7 animate-spin text-[#0F2D5C]" />
              ) : (
                <Upload className="w-7 h-7 text-slate-400" />
              )}
              <span className="text-xs font-medium text-slate-700">
                {csfBusy ? "Leyendo PDF…" : "Seleccionar PDF de constancia"}
              </span>
              <input
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                disabled={csfBusy || busy}
                onChange={onCsfPdfSelected}
                data-testid="registro-csf-file-input"
              />
            </label>
            {csfWarnings.length > 0 && (
              <ul className="text-[10px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 space-y-1 list-disc pl-4">
                {csfWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold">Razón social o nombre *</Label>
              <Input
                required
                value={form.razon_social}
                onChange={(e) => setForm({ ...form, razon_social: e.target.value })}
                className="mt-1 h-9 text-sm"
                placeholder="Según constancia de situación fiscal"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">RFC *</Label>
              <Input
                required
                value={form.rfc}
                onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                className="mt-1 h-9 text-sm font-mono"
                maxLength={14}
              />
            </div>
            <div>
              <Label className="text-xs">Nombre comercial</Label>
              <Input
                value={form.nombre_comercial}
                onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Domicilio fiscal</Label>
              <Input
                value={form.domicilio}
                onChange={(e) => setForm({ ...form, domicilio: e.target.value })}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Código postal</Label>
              <Input
                value={form.codigo_postal}
                onChange={(e) => setForm({ ...form, codigo_postal: e.target.value })}
                className="mt-1 h-9 text-sm font-mono"
                maxLength={5}
              />
            </div>
            <div>
              <Label className="text-xs">Régimen fiscal</Label>
              <Select value={form.regimen_fiscal} onValueChange={(v) => setForm({ ...form, regimen_fiscal: v })}>
                <SelectTrigger className="mt-1 h-9 text-xs">
                  <SelectValue placeholder="Seleccione" />
                </SelectTrigger>
                <SelectContent>
                  {REGIMENES_SAT.map((r) => (
                    <SelectItem key={r.c} value={r.c}>
                      {formatRegimenSatSelectLabel(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Responsable / contacto</Label>
              <Input
                value={form.responsable}
                onChange={(e) => setForm({ ...form, responsable: e.target.value })}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Teléfono</Label>
              <Input
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Correo de facturación</Label>
              <Input
                type="email"
                value={form.correo_facturacion}
                onChange={(e) => setForm({ ...form, correo_facturacion: e.target.value })}
                className="mt-1 h-9 text-sm font-mono"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Correo para acceso al sistema *</Label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 h-9 text-sm font-mono"
                placeholder="Será su usuario de inicio de sesión"
              />
            </div>
            <div>
              <Label className="text-xs">Usuario corto (opcional)</Label>
              <Input
                value={form.new_portal_username}
                onChange={(e) =>
                  setForm({
                    ...form,
                    new_portal_username: e.target.value.toLowerCase().replace(/\s+/g, ""),
                  })
                }
                className="mt-1 h-9 text-xs font-mono"
                placeholder="ej. mi_despacho (o vacío = solo correo)"
              />
              <p className="text-[10px] text-slate-500 mt-1">Puede entrar con este usuario o con su correo.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold">Nueva contraseña *</Label>
              <Input
                required
                type="password"
                autoComplete="new-password"
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                className="mt-1 h-9 text-sm font-mono"
                minLength={6}
                maxLength={128}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Confirmar contraseña *</Label>
              <Input
                required
                type="password"
                autoComplete="new-password"
                value={form.new_password2}
                onChange={(e) => setForm({ ...form, new_password2: e.target.value })}
                className="mt-1 h-9 text-sm font-mono"
                minLength={6}
                maxLength={128}
              />
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4 space-y-3">
            <div>
              <Label className="text-xs font-semibold text-[#0F2D5C]">Recuperación de contraseña *</Label>
              <p className="text-[11px] text-slate-600 mt-1 leading-snug">
                Seleccione <strong>3 preguntas</strong> del catálogo y una respuesta por cada una. Si olvida la
                contraseña, deberá acertar al menos una.
              </p>
            </div>
            <SecurityRecoveryQuestionFields
              rows={secRows}
              onChange={setSecRows}
              disabled={busy}
              testIdPrefix="registro-inicial-security"
            />
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full h-11 bg-[#0F2D5C] hover:bg-[#0a1f42] rounded-sm mt-4"
            data-testid="registro-inicial-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Guardar y continuar
          </Button>
        </form>
      </div>
    </div>
  );
}
